import React from 'react';
import { ArrowUpDown, ChevronDown, ChevronUp } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Checkbox } from '@/components/ui/checkbox';
import { OtrosTramiteRow } from './OtrosTramiteRow';
import { OtrosTramiteCard } from './OtrosTramiteCard';
import { useIsMobile } from '@/hooks/use-mobile';
import { useOtrosTramitesColumnVisibility } from '../contexts/OtrosTramitesColumnVisibilityContext';
import type {
  OtroTramiteRecord,
  OtroTramiteSortDirection,
  OtroTramiteSortField,
} from '../types';

interface SortConfig {
  field: OtroTramiteSortField;
  direction: OtroTramiteSortDirection;
}

interface OtrosTramitesTableProps {
  tramites: OtroTramiteRecord[];
  isLoading: boolean;
  isError?: boolean;
  sortConfig: SortConfig;
  onSortChanged: (field: OtroTramiteSortField) => void;
  onView: (tramite: OtroTramiteRecord) => void;
  onEdit: (tramite: OtroTramiteRecord) => void;
  onDelete: (tramite: OtroTramiteRecord) => void;
  onRetirar: (tramite: OtroTramiteRecord) => void;
  onViewRetiroHistorial?: (tramite: OtroTramiteRecord) => void;
  onManageCobros?: (tramite: OtroTramiteRecord) => void;
  onManageFacturas?: (tramite: OtroTramiteRecord) => void;
  onManageArchivos?: (tramite: OtroTramiteRecord) => void;
  onSendWhatsAppRetiro?: (tramite: OtroTramiteRecord) => void;
  onViewOtorgantes?: (tramite: OtroTramiteRecord) => void;
  onViewResponsables?: (tramite: OtroTramiteRecord) => void;
  onViewGarantes?: (tramite: OtroTramiteRecord) => void;
  onEditAssignedUser?: (tramite: OtroTramiteRecord) => void;
  selectedIds: Set<string>;
  onToggleSelect: (id: string) => void;
  onToggleAll: () => void;
}

const SORT_OPTIONS: Array<{ field: OtroTramiteSortField; label: string }> = [
  { field: 'id', label: 'ID' },
  { field: 'fecha', label: 'Fecha' },
  { field: 'otorgantes', label: 'Otorgantes' },
  { field: 'responsables', label: 'Responsables' },
  { field: 'garantes', label: 'Garantes' },
  { field: 'tipo', label: 'Tipo' },
];

export const OtrosTramitesTable: React.FC<OtrosTramitesTableProps> = ({
  tramites,
  isLoading,
  isError = false,
  sortConfig,
  onSortChanged,
  onView,
  onEdit,
  onDelete,
  onRetirar,
  onViewRetiroHistorial,
  onManageCobros,
  onManageFacturas,
  onManageArchivos,
  onSendWhatsAppRetiro,
  onViewOtorgantes,
  onViewResponsables,
  onViewGarantes,
  onEditAssignedUser,
  selectedIds,
  onToggleSelect,
  onToggleAll,
}) => {
  const { isColumnVisible, getVisibleColumns } = useOtrosTramitesColumnVisibility();
  const isMobile = useIsMobile();
  const eligibleIds = tramites
    .filter((t) => t.copiasTotales - t.copiasActuales > 0)
    .map((t) => t.id);
  const allSelected = eligibleIds.length > 0 && eligibleIds.every((id) => selectedIds.has(id));
  const someSelected = eligibleIds.some((id) => selectedIds.has(id));
  const headerChecked: boolean | 'indeterminate' = allSelected ? true : someSelected ? 'indeterminate' : false;
  const getSortIcon = (field: OtroTramiteSortField) => {
    if (sortConfig.field !== field) {
      return <ArrowUpDown className="ml-1 h-4 w-4 text-gray-400" />;
    }

    return sortConfig.direction === 'asc'
      ? <ChevronUp className="ml-1 h-4 w-4 text-gray-600" />
      : <ChevronDown className="ml-1 h-4 w-4 text-gray-600" />;
  };

  const SortableHeader = ({
    field,
    children,
  }: {
    field: OtroTramiteSortField;
    children: React.ReactNode;
  }) => (
    <TableHead
      className="cursor-pointer select-none hover:bg-gray-100"
      onClick={() => onSortChanged(field)}
    >
      <div className="flex items-center justify-between gap-2">
        <span>{children}</span>
        {getSortIcon(field)}
      </div>
    </TableHead>
  );

  const rowHandlers = {
    onView,
    onEdit,
    onDelete,
    onRetirar,
    onViewRetiroHistorial,
    onManageCobros,
    onManageFacturas,
    onManageArchivos,
    onSendWhatsAppRetiro,
    onViewOtorgantes,
    onViewResponsables,
    onViewGarantes,
    onEditAssignedUser,
    onToggleSelect,
  };

  const emptyMessage = isError
    ? 'No pudimos cargar los trámites en este momento.'
    : 'No encontramos trámites que coincidan con los filtros seleccionados.';

  if (isMobile) {
    const sortableFields = SORT_OPTIONS.filter((option) => isColumnVisible(option.field));

    return (
      <section className="space-y-2">
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-gray-100 bg-white px-3 py-2 shadow-sm">
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={headerChecked}
              onCheckedChange={onToggleAll}
              disabled={eligibleIds.length === 0}
              aria-label="Seleccionar todos"
            />
            Todos
          </label>
          {sortableFields.length > 0 && (
            <div className="ml-auto flex items-center gap-1">
              <select
                aria-label="Ordenar por"
                className="h-8 rounded-md border border-input bg-white px-2 text-base"
                value={sortConfig.field}
                onChange={(event) => onSortChanged(event.target.value as OtroTramiteSortField)}
              >
                {!sortableFields.some((option) => option.field === sortConfig.field) && (
                  <option value={sortConfig.field} hidden>Ordenar</option>
                )}
                {sortableFields.map((option) => (
                  <option key={option.field} value={option.field}>{option.label}</option>
                ))}
              </select>
              <button
                type="button"
                className="flex h-8 w-8 items-center justify-center rounded-md border border-input"
                onClick={() => onSortChanged(sortConfig.field)}
                aria-label={sortConfig.direction === 'asc' ? 'Orden ascendente' : 'Orden descendente'}
                title="Invertir orden"
              >
                {sortConfig.direction === 'asc'
                  ? <ChevronUp className="h-4 w-4" />
                  : <ChevronDown className="h-4 w-4" />}
              </button>
            </div>
          )}
        </div>

        {isLoading && Array.from({ length: 4 }).map((_, index) => (
          <Skeleton key={`skeleton-card-${index}`} className="h-36 w-full rounded-lg" />
        ))}

        {!isLoading && tramites.map((tramite) => (
          <OtrosTramiteCard
            key={tramite.id}
            tramite={tramite}
            {...rowHandlers}
            isSelected={selectedIds.has(tramite.id)}
          />
        ))}

        {!isLoading && tramites.length === 0 && (
          <div className="rounded-xl border border-gray-100 bg-white px-4 py-16 text-center text-gray-500 shadow-sm">
            {emptyMessage}
          </div>
        )}
      </section>
    );
  }

  return (
    <section className="rounded-xl border border-gray-100 bg-white shadow-sm">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50">
              <TableHead className="w-10">
                <Checkbox
                  checked={headerChecked}
                  onCheckedChange={onToggleAll}
                  disabled={eligibleIds.length === 0}
                  aria-label="Seleccionar todos"
                />
              </TableHead>
              {isColumnVisible('id') && <SortableHeader field="id">ID</SortableHeader>}
              {isColumnVisible('fecha') && <SortableHeader field="fecha">Fecha</SortableHeader>}
              {isColumnVisible('otorgantes') && <SortableHeader field="otorgantes">Otorgantes</SortableHeader>}
              {isColumnVisible('responsables') && <SortableHeader field="responsables">Responsables</SortableHeader>}
              {isColumnVisible('garantes') && <SortableHeader field="garantes">Garantes</SortableHeader>}
              {isColumnVisible('asignados') && <TableHead>Asignados</TableHead>}
              {isColumnVisible('tipo') && <SortableHeader field="tipo">Tipo</SortableHeader>}
              {isColumnVisible('detalle') && <TableHead>Detalle</TableHead>}
              {isColumnVisible('copias') && <TableHead>Copias</TableHead>}
              {isColumnVisible('factura') && <TableHead>Factura</TableHead>}
              {isColumnVisible('archivos') && <TableHead>Archivos</TableHead>}
              {isColumnVisible('indicadores') && <TableHead>Indicadores</TableHead>}
              {isColumnVisible('acciones') && <TableHead className="text-right">Acciones</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              Array.from({ length: 6 }).map((_, rowIndex) => (
                <TableRow key={`skeleton-row-${rowIndex}`} className="border-b border-gray-100">
                  {Array.from({ length: getVisibleColumns().length + 1 }).map((__, cellIndex) => (
                    <TableCell key={`skeleton-cell-${rowIndex}-${cellIndex}`}>
                      <Skeleton className="h-4 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}

            {!isLoading && tramites.map((tramite) => (
              <OtrosTramiteRow
                key={tramite.id}
                tramite={tramite}
                onView={onView}
                onEdit={onEdit}
                onDelete={onDelete}
                onRetirar={onRetirar}
                onViewRetiroHistorial={onViewRetiroHistorial}
                onManageCobros={onManageCobros}
                onManageFacturas={onManageFacturas}
                onManageArchivos={onManageArchivos}
                onSendWhatsAppRetiro={onSendWhatsAppRetiro}
                onViewOtorgantes={onViewOtorgantes}
                onViewResponsables={onViewResponsables}
                onViewGarantes={onViewGarantes}
                onEditAssignedUser={onEditAssignedUser}
                isSelected={selectedIds.has(tramite.id)}
                onToggleSelect={onToggleSelect}
              />
            ))}

            {!isLoading && tramites.length === 0 && (
              <TableRow>
                <TableCell colSpan={getVisibleColumns().length + 1} className="py-40 text-center text-gray-500">
                  {emptyMessage}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </section>
  );
};
